import utcToZonedTime from 'date-fns-tz/utcToZonedTime';
import format from 'date-fns/format';
import { getUserTimezoneAbbreviation } from 'shared/utils';

interface TimeProps {
  hour: number;
  minute: number;
}

// times are stored in "00:00:00" string format in UTC
const getHourAndMinuteFromTimeString = (timeString: string): TimeProps => {
  const segments = timeString.split(':');
  if (segments.length !== 3) {
    console.warn('Invalid time string provided. Format must match "00:00:00".');
    return { hour: 0, minute: 0 };
  }
  return {
    hour: Number(segments[0]),
    minute: Number(segments[1]),
  };
};

export const getDateFromTimeString = (timeString: string, baseDate?: string | Date): Date => {
  const time = getHourAndMinuteFromTimeString(timeString);
  const base = !!baseDate ? new Date(baseDate) : new Date();
  const date = base.setUTCHours(time.hour, time.minute, 0, 0);
  return new Date(date);
};

// when updating this logic consider using
// https://github.com/marnusw/date-fns-tz?tab=readme-ov-file#gettimezoneoffset
export const getFormattedDateFromTimeString = (timeString: string, timeZone?: string, baseDate?: string): string => {
  let date = getDateFromTimeString(timeString, baseDate);
  if (isNaN(date.getTime())) {
    console.warn('Invalid date passed to getFormattedDateFromTimeString');
    return 'Invalid Date';
  }
  // prevent internal server error when input is not expected format
  if (timeZone) {
    date = utcToZonedTime(date, timeZone);
  }

  if (baseDate) {
    const time = date.getTime();
    date = new Date(new Date().setTime(time));
  }

  return format(date, 'h:mmaaa');
};

/**
 * Format an ISO 8601 string date to a specific time and time zone.
 *
 * @param timeString - The ISO 8601 date string to be formatted.
 * @returns A string representing the formatted date and time.
 * @example formatTimesStringToTimeZone('2021-01-01T00:00:00.000Z') // 'Thursday, December 31st, 7:00 pm CDT'
 */
export const formatTimesStringToDateStringWithTimeZone = (timeString: string): string => {
  const date = new Date(timeString);

  if (isNaN(date.getTime())) {
    console.warn('Invalid date passed to formatTimesStringToDateStringWithTimeZone');
    return 'Invalid Date';
  }

  // Format the zonedDate without specifying timeZone, as it's already in the correct time zone
  return format(date, `EEEE, MMMM do, h:mm aaaa '${getUserTimezoneAbbreviation()}'`);
};

export const getUTCTimeStringFromDate = (date: Date): string => {
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  return `${hours < 10 ? '0' : ''}${hours}:${minutes < 10 ? '0' : ''}${minutes}:00`;
};

export const sanitizeDate = (date: Date | string): Date => {
  const sanitized = new Date(date).setFullYear(2000, 1, 1);
  return new Date(sanitized);
};
